export type ImageLoadingMode = 'eager' | 'lazy'
export type ImageFetchPriority = 'high' | 'low' | 'auto'

export type UseViewportImageLoadingOptions = {
  /** 已知 LCP / 必为首屏时强制 eager */
  forceEager?: boolean
  fetchPriority?: ImageFetchPriority
  /** 首屏底部额外缓冲（px），默认 120，避免贴边模块被误判 lazy */
  rootMarginBottom?: number
}

const resolveEl = (target: unknown): HTMLElement | null => {
  if (!target) return null
  if (target instanceof HTMLElement) return target
  if (
    typeof target === 'object' &&
    target !== null &&
    '$el' in target &&
    (target as { $el: unknown }).$el instanceof HTMLElement
  ) {
    return (target as { $el: HTMLElement }).$el
  }
  return null
}

/**
 * 按当前视口高度动态决定图片 loading：
 * - 非 LCP：SSR / 初始默认 lazy，避免 HTML 里屏外图被浏览器抢先下载
 * - 挂载后若落在首屏（含底部缓冲）再升为 eager
 * - forceEager：始终 eager（banner / LCP）
 */
export const useViewportImageLoading = (
  options: UseViewportImageLoadingOptions = {},
) => {
  const elRef = ref<HTMLElement | ComponentPublicInstance | null>(null)
  const loading = ref<ImageLoadingMode>(
    options.forceEager ? 'eager' : 'lazy',
  )
  const fetchPriority = ref<ImageFetchPriority>(
    options.forceEager
      ? (options.fetchPriority ?? 'high')
      : (options.fetchPriority ?? 'auto'),
  )

  const resolveLoading = () => {
    if (options.forceEager) {
      loading.value = 'eager'
      return
    }
    if (import.meta.server) return

    const el = resolveEl(elRef.value)
    if (!el) return

    const rect = el.getBoundingClientRect()
    const threshold =
      window.innerHeight + (options.rootMarginBottom ?? 120)
    const isFirstScreen = rect.top < threshold && rect.bottom > 0
    loading.value = isFirstScreen ? 'eager' : 'lazy'
  }

  onMounted(() => {
    if (options.forceEager) {
      loading.value = 'eager'
      return
    }
    resolveLoading()
    requestAnimationFrame(resolveLoading)
  })

  return {
    elRef,
    loading,
    fetchPriority,
    resolveLoading,
  }
}
